import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, defer, forkJoin, map, of, switchMap, take } from 'rxjs';
import { HttpService } from 'src/app/core/services/http/http.service';
import { ContactInfo } from 'src/app/interfaces/contact-info.interface';
import { APIConstants } from '../services.constants';
import { MailOrderFamilyMember, MailOrderProfile } from 'src/app/interfaces/mail-order-profile.interface';
import { UserService } from '../user/user.service';
import { NotificationsBody } from 'src/app/interfaces/notifications-body.interface';
import { ShippingAddressesResponse } from 'src/app/interfaces/shipping-addresses-response.interface';
import { RequestOptions } from 'src/app/interfaces';
import { IPmartProfileEnrollRequest } from '../../interfaces/request/pmart-profile-enroll-request.interface';
import { CmsService } from '../cms/cms.service';

@Injectable({
  providedIn: 'root'
})
export class MailOrderProfileEnrollmentService {

  public showUpdateProfileMessage$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true) // flag to enable and disable update dialog on prescriptions screen
  public hideUpdateProfileMessage$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  public disableMailOrderFeatures$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  constructor(private readonly httpService: HttpService, private userService: UserService, private cmsService: CmsService) { }

  public getMembersToUpdateProfile(): Observable<MailOrderFamilyMember[]> {
    if (this.userService.familyCoverageResponse && this.userService.eligibilityVerificationResponse && this.userService.eligibilityVerificationResponse?.clientId) {
      const clientId = this.userService.eligibilityVerificationResponse?.clientId;
      const cardId = this.userService.eligibilityVerificationResponse?.cardId;

      const memberDetailsArray = this.userService.familyCoverageResponse.members.map((member) => {
        return this.searchProfile(clientId, cardId, member.personCode).pipe(map((result) => {
          const memberDetails: MailOrderFamilyMember = {
            memberProfile: member,
            enrolledInPharmacySystem: result,
          }
          return memberDetails;
        }));
      })
      return memberDetailsArray.length > 0 ? forkJoin(memberDetailsArray) : of([]);
    }
    return of([]);
  }

  /**
   * Checks if members client id is configured in WP - "SUPPRESS_MAIL_ORDER_PROFILE_FOR_CLIENTS"
   * @param memberClientId Member's client ID
   * @returns 
   */
  public suppressMailOrderProfileByClientId(memberClientId: string): Observable<boolean> {
    return this.cmsService.loadConfigurations(memberClientId).pipe(
      take(1),
      map((cmsConfig) => cmsConfig.clientSpecificConfig.suppressMailOrderFeatures)
    );
  }

  public showPMartFormByClientId(memberClientId: string): Observable<boolean> {
    return this.cmsService.loadConfigurations(memberClientId).pipe(
      take(1),
      map((cmsConfig) => cmsConfig.clientSpecificConfig.enablePMartForm)
    );
  }

  /**
   * Enables the mail order profile creation form for - 
   * 1. All the Orion clients, except the clients configured in wordpress - "SUPPRESS_MAIL_ORDER_PROFILE_FOR_CLIENTS"
   * 2. Benecard clients configured in wordpress - "BENECARD_CLIENTS_TO_ENABLE_PMART_FORM"
   * @returns
   */
  public enableMailOrderProfileDialog(clientId: string): Observable<boolean> {
    try {
      return defer(() => {
        if (this.userService.isOrionUser()) {
          return this.suppressMailOrderProfileByClientId(clientId).pipe(map((suppress) => {
            return suppress ? false : true;
          }), catchError(() => {
            return of(true);
          }));
        } else {
          return this.showPMartFormByClientId(clientId).pipe(map((show) => {
            return show ? true : false;
          }))
        }
      });
    } catch (error) {
      return of(false);
    }
  }

  public showUpdateProfileDialog(): Observable<boolean> {
    const clientId = this.userService.eligibilityVerificationResponse?.clientId;
    const cardId = this.userService.eligibilityVerificationResponse?.cardId;
    const personCode = this.userService.eligibilityVerificationResponse?.personCode;

    if (!clientId || !cardId || !personCode) return of(false);

    return this.enableMailOrderProfileDialog(clientId).pipe(switchMap((enableMailOrderProfile) => {
      if (enableMailOrderProfile) {
        return this.searchProfile(clientId, cardId, personCode).pipe(map((res) => {
          return res ? false : true
        }), catchError(() => {
          return of(false);
        }));
      } else {
        return of(false);
      }
    }));
  }

  public getContactSSOInfo(clientId: string, cardId: string, personCode: string): Observable<ContactInfo> {
    return this.httpService.get<ContactInfo>(`${APIConstants.USER_MGMT_SERVICES.GET_CONTACT_INFO}?clientId=${clientId}&cardId=${cardId}&personCode=${personCode}`);
  }

  public getContactInfo(clientId: string, cardId: string, personCode: string): Observable<ContactInfo> {
    return this.httpService.get<ContactInfo>(`${APIConstants.USER_MGMT_SERVICES.GET_CONTACT_INFO}?clientId=${clientId}&cardId=${cardId}&personCode=${personCode}`);
  }

  public createProfile(request: IPmartProfileEnrollRequest): Observable<{ message: string, statusCode: string }> {
    return this.httpService.post<{ message: string, statusCode: string }>(`${APIConstants.MAIL_ORDER_PROFILE_ENROLLMENT.CREATE_PROFILE}`, request);
  }

  public searchProfile(clientId: string, cardId: string, personCode: string):
    Observable<boolean> {
    const requestBody = {
      cardId: cardId,
      personCode: personCode,
      clientID: clientId
    }

    return this.httpService.post<MailOrderProfile>(`${APIConstants.MAIL_ORDER_PROFILE_ENROLLMENT.SEARCH_PROFILE}`, requestBody).pipe(map((res) => res.enrolledInPharmacySystem === true ? true : false));
  }

  public getNotificationSettings(clientId: string, cardId: string, personCode: string): Observable<NotificationsBody> {
    return this.httpService.get<NotificationsBody>(`${APIConstants.MEMBER_SERVICES.GET_NOTIFICATION_SETTINGS}?clientId=${clientId}&cardId=${cardId}&personCode=${personCode}`).pipe(map((res) => res));
  }

  public getShippingAddresses(clientId: string, cardId: string, personCode: string): Observable<ShippingAddressesResponse> {
    return this.httpService.get<ShippingAddressesResponse>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_SHIPPING_ADDRESS}?clientId=${clientId}&cardId=${cardId}&personCode=${personCode}`);
  }

  public updateNotificationSettings(clientId: string, cardId: string, personCode: string, ShippingSetting: string): Observable<NotificationsBody> {
    return this.getNotificationSettings(clientId, cardId, personCode).pipe(switchMap((res) => {
      res.mailOrderShippingNotification = ShippingSetting;
      return this.updateNotificationSettingsCall(clientId, cardId, personCode, res).pipe();
    }));
  }

  public updateNotificationSettingsCall(clientId: string, cardId: string, personCode: string, notification: NotificationsBody): Observable<NotificationsBody> {
    return this.httpService.put<NotificationsBody>(`${APIConstants.MEMBER_SERVICES.GET_NOTIFICATION_SETTINGS}?clientId=${clientId}&cardId=${cardId}&personCode=${personCode}`, notification, { responseType: 'text' } as RequestOptions);
  }
}
